import React, { useState, useEffect, useRef } from 'react';
import styles from './LoanCalculatorPage.scss';
import LoanCalculatorTeaser from './LoanCalculatorTeaser';
import LoanCalculatorWidget from './LoanCalculatorWidget';
import LoanChooseBank from './LoanChooseBank';
import LoanContact from './LoanContact';
import LoanRequest, { ILoanRequestAddSubmit, ILoanRequestLoanCalculator } from '../../utils/LoanRequest';
import History from '../../utils/BrowserHistory';
import reactComponent from '../../utils/CreateComponent';
import HomeDetails from './HomeDetails';
import IHome from '../../interfaces/IHome';
import SyncFixedBottom from '../SyncFixedBottom';
import Responsive from '../../utils/Responsive';
import LoadingIndicator from '../LoadingIndicator';
import Config from '../../utils/Config';
import { ICalculatorValues } from './Calculator';
import LoanUserDetails from './LoanUserDetails';
import LoanCalculationIndicator from './LoanCalculationIndicator';
import merge from 'lodash/merge';
import LogoPSD from '../../assets/img/psd_bank.svg';
import trackApi from '../../utils/trackApi';
import LoanGetBanks from './LoanGetBanks';
import { ILoanOffer } from '../../utils/BaufiSmartApi';
import BaufiLeadApi from '../../utils/BaufiLeadApi';
import Markdown from '../Markdown';
import Affiliate from '../../utils/Affiliate';

const loanCalculatorRoute = Config.get('loanCalculatorRoute');

export interface ILoanCalculatorPageProps {
    uuid: string;
    price?: number;
    calcParams?: ICalculatorValues;
    provider?: string;
    textKeys?: ILoanCalculatorTextKeys;
}

interface IStep2Labels {
    income: string;
    occupation: string;
    time: string;
    usage: string;
}

interface IStep4Option {
    key: string;
    name: string;
    description: string;
    button: string;
    image?: any;
}

interface IStepKey {
    headline: string;
    description?: string;
    button?: string;
    trackingName: string;
    // labels?: IStep2Labels;
    // options?: IStep4Option[];
}

export interface ILoanCalculatorTextKeys {
    step1: IStepKey;
    // step2: IStepKey & { labels: IStep2Labels };
    step2: IStepKey;
    step3: IStepKey;
    // step3: Omit<IStepKey, 'button'> & { options: IStep4Option[] };
    step4: IStepKey;
    step5: IStepKey & { text: string };
}

const defaultTextKeys: ILoanCalculatorTextKeys = {
    step1: {
        headline: 'Finanzierungsrechner für Deinen Immobilienkredit',
        button: 'Wer würde mich finanzieren?',
        trackingName: 'finanzierungsrechner',
    },
    // step2: {
    //     headline: 'Persönliche Daten',
    //     description:
    //         'Banken haben unterschiedliche Auswahlkriterien, wenn es um die Vergabe von Immobilienkrediten geht. Beispielsweise finanzieren einige Banken lieber Personen in einem Angestelltenverhältnis als Freiberufler oder bieten je nach Nutzung der Immobilie andere Finanzierungsmodelle an. Deswegen benötigen wir einige Angaben zu Deiner Person, um Dir einen passenden Finanzierungspartner vorzuschlagen!',
    //     button: 'Finanzierungspartner suchen',
    //     labels: {
    //         occupation: 'Beschäftigungsverhältnis*:',
    //         income: 'Haushaltsnettoeinkommen pro Monat*:',
    //         usage: 'Geplante Nutzung*:',
    //         time: 'Wann soll finanziert werden?*',
    //     },
    // },
    step2: {
        headline: 'Einen kleinen Moment Geduld.',
        description: 'Wir suchen gerade den passenden Finanzierungspartner für Dich!',
        trackingName: 'lade_banken',
    },
    step3: {
        headline: 'Mögliche Finanzierungspartner für Dich',
        trackingName: 'auswahl_bank',
        // options: [
        //     {
        //         key: 'PsdBankNord',
        //         name: 'PSD Bank Nord eG',
        //         image: LogoPSD,
        //         description:
        //             'Finanziere Dein neues Zuhause bei einer starken Genossenschaftsbank, die über mehr als 145 Jahre Erfahrung und Kompetenz im Privatkundengeschäft verfügt.',
        //         button: 'Kostenlos und unverbindlich anfragen',
        //     },
        //     {
        //         key: 'other',
        //         name: 'Andere Bank',
        //         description: 'Die obere Auswahl sagt Dir nicht zu? Kein Problem - lass uns zusammen nach einer Alternative für Deine Finanzierung suchen!',
        //         button: 'Kostenlos und unverbindlich weitersuchen',
        //     },
        // ],
    },
    step4: {
        headline: 'Gute Wahl! Wie dürfen wir Dich kontaktieren?',
        description:
            'Um weitere Details mit Dir zu besprechen und Dich unabhängig von Banken beraten zu können, braucht unser Partner des Vertrauens in Finanzierungsfragen *accedo* noch weitere Angaben von Dir. Mit dem Klick auf "Unverbindlich beraten lassen" übermitteln wir Deine Anfrage an die  *accedo*-Baufinanzierungsexperten, die Dir kostenlos und unverbindlich ein maßgeschneidertes Baufinanzierungskonzept entwickeln.',
        button: 'Unverbindlich beraten lassen',
        trackingName: 'user_details',
    },
    step5: {
        headline: 'Du hast es geschafft. Vielen Dank!',
        text: `Dein *accedo*-Baufinanzierungsexperte wird sich zeitnah mit Dir in Verbindung setzen.
Hast du noch Fragen zu Deiner Traum-Immobilie? Wir freuen uns auf Deinen Kontakt. Du erreichst uns jederzeit unter [service@wohnglueck.de](mailto:service@wohnglueck.de).

Dein Team von wohnglueck.de`,
        trackingName: 'danke',
    },
};

const LoanCalculatorPage: React.FC<React.PropsWithChildren<ILoanCalculatorPageProps>> = ({ uuid, price: price = null, calcParams = null, provider = null, textKeys = null }) => {
    const [step, setStep] = React.useState(1);
    const [data, setData] = React.useState<ILoanRequestAddSubmit>({ propertyId: uuid, provider });
    const [calcPrice, setCalcPrice] = React.useState(price === null ? 100000 : price);
    const [loanOffers, setLoanOffers] = React.useState<ILoanOffer[]>(null);

    const topRef = React.useRef(null);

    React.useEffect(() => {
        History.push({
            hash: '#step0',
        });
        const unlisten = History.listen(({ location, action }) => {
            const step = location.hash ? parseInt(location.hash.replace('#step', '')) : 1;
            setStep(step);
        });
        return unlisten;
    }, []);

    React.useEffect(() => {
        if (step > 1) {
            window.scrollTo(0, topRef.current.offsetTop - 50);
        }
        trackApi.pageview(`${window.location.pathname}?step=${defaultTextKeys[`step${step}`].trackingName}`);
        // trackApi.pageview(`/${loanCalculatorRoute}?step=${defaultTextKeys[`step${step}`].trackingName}`);
    }, [step]);

    const handleNextStep = (newData: any) => {
        if (step < 1) {
        } else if (step === 2) {
            setLoanOffers(newData);
        } else {
            const nextData = { ...data, ...newData, affiliate: Affiliate.getFromCookie() };
            setData(nextData);

            if (step === 4) {
                BaufiLeadApi.addLead(nextData, data.id)
                    .then((res) => {
                        const baufiLeadId = res.vorgangsnummer;
                        LoanRequest.add({ ...nextData, baufiLeadId })
                            .then((resData) => {
                                setData({ ...nextData, baufiLeadId });
                                setStep(step + 1);
                                // trackApi.event('Finanzierungsrechner', 'Klick', 'Lead');
                                // trackApi.wglTarget('DASHBOARD');
                            })
                            .catch((e) => {
                                setStep(step + 1);
                            });
                    })
                    .catch((e) => {
                        console.error(e);
                        LoanRequest.add({ ...nextData, baufiLeadId: 'Baufi Request Failed!' })
                            .then((resData) => {
                                setData({ ...nextData });
                                setStep(step + 1);
                                // trackApi.wglTarget('DASHBOARD');
                                // trackApi.event('Finanzierungsrechner', 'Klick', 'Lead');
                            })
                            .catch((e) => {
                                setStep(step + 1);
                            });
                    });
            } else {
                LoanRequest.add(nextData)
                    .then((resData) => {
                        setData({ ...nextData, id: resData.id });
                        setStep(step + 1);
                        // trackApi.event('Finanzierungsrechner', 'send', 'Form');
                    })
                    .catch((e) => {
                        setStep(step + 1);
                    });
            }
        }

        History.push({
            hash: '#step' + (step + 1),
        });
    };

    const handlePreviousStep = () => {
        let prevStep = step - 1;

        if (prevStep === 2) {
            prevStep--;
        }
        setStep(prevStep);

        History.push({
            hash: '#step' + prevStep,
        });
    };

    const handleHomeLoaded = (home: IHome) => {
        if (!price && home.price) {
            setCalcPrice(home.price);
        }
        setData({ ...data, property: home });
    };

    const currTextKeys: ILoanCalculatorTextKeys = merge(defaultTextKeys, textKeys);

    return (
        <div className={styles.loanCalculatorPage} ref={topRef}>
            {uuid && (
                <SyncFixedBottom
                    syncWith={topRef}
                    className={styles.home}
                    active={Responsive.getWidth() >= 993}
                    topOffset={65}
                    bottomOffset={45}
                    alignment="top"
                >
                    <HomeDetails onLoaded={handleHomeLoaded} key={uuid} uuid={uuid} />
                </SyncFixedBottom>
            )}

            <h1>{currTextKeys[`step${step}`]?.headline}</h1>
            <Markdown>{currTextKeys[`step${step}`]?.description}</Markdown>

            {step === 1 && (
                <LoanCalculatorWidget
                    syncRef={topRef}
                    data={data.loanCalculator}
                    onNextStep={handleNextStep}
                    price={calcPrice}
                    calcParams={calcParams}
                    // uuid={uuid}
                    textKeys={currTextKeys.step1}
                ></LoanCalculatorWidget>
            )}
            {/* {step === 2 && <LoanChooseBank data={data.chooseBank} onNextStep={handleNextStep}></LoanChooseBank>} */}
            {/* {step === 2 && (
                <LoanUserDetails
                    data={data.loanDetails}
                    onNextStep={handleNextStep}
                    onPreviousStep={handlePreviousStep}
                    textKeys={currTextKeys.step2}
                ></LoanUserDetails>
            )} */}
            {/* {step === 3 && <LoanCalculationIndicator onNextStep={handleNextStep} />} */}
            {step === 2 && <LoanGetBanks data={data} onNextStep={handleNextStep} onPreviousStep={handlePreviousStep} />}
            {step === 3 && <LoanChooseBank loanOffers={loanOffers} onNextStep={handleNextStep} onPreviousStep={handlePreviousStep} />}
            {step === 4 && (
                <LoanContact syncRef={topRef} data={data} onNextStep={handleNextStep} onPreviousStep={handlePreviousStep} textKeys={currTextKeys.step4} />
            )}
            {step === 5 && (
                <div>
                    <Markdown>{currTextKeys.step5.text}</Markdown>
                </div>
            )}
        </div>
    );
};

export default LoanCalculatorPage;
